import { useState, useEffect } from 'react';
import { getSubscriptionPlans, saveSubscriptionPlan } from '../components/DbServices';
import Layout from '../layout/Layout';
import PageTransition from '../components/PageTransition';
import Swal from 'sweetalert2';

export default function ManagePlans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '', price: '', device_limit: '', message_limit: '', duration_days: '30'
  });

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const res = await getSubscriptionPlans(null);
      // Accessing res.data.plans based on your typical API wrapper
      setPlans(res.data?.plans || res.plans || []);
    } catch (err) {
      console.error("Failed to fetch plans", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlans(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await saveSubscriptionPlan(formData);
      if (res.status === 200 || res.data?.status === 200) {
        Swal.fire("Plan created successfully!");
        setFormData({ name: '', price: '', device_limit: '', message_limit: '', duration_days: '30' });
        fetchPlans();
      }
    } catch (err) {
      Swal.fire("Failed to save plan.");
    }
  };

  return (
    <Layout>
      <PageTransition>
        <div className="container-fluid py-4">
          
          {/* HEADER SECTION - Finalized Style */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1"><span className="color-theme">Plan Management</span> 💎</h3>
              <p className="text-muted small fw-medium">Configure and update your subscription offerings.</p>
            </div>
          </div>

          <div className="row g-4">
            {/* Create Plan Form - Styled like your Controller Cards */}
            <div className="col-lg-4">
              <div className="card border-0 shadow-sm rounded-4 p-4 sticky-top" style={{ top: '20px' }}>
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary-subtle text-primary p-2 rounded-circle me-3">
                    <i className="fas fa-plus-circle fa-lg"></i>
                  </div>
                  <h6 className="fw-bold mb-0">Create New Plan</h6>
                </div>
                
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="text-muted small fw-bold text-uppercase mb-1">Plan Name</label>
                    <input type="text" className="form-control rounded-3 border-light-subtle bg-light-subtle small py-2" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="e.g. Enterprise Pro" required />
                  </div>
                  
                  <div className="row mb-3 g-2">
                    <div className="col-6">
                      <label className="text-muted small fw-bold text-uppercase mb-1">Price (Rs.)</label>
                      <input type="number" className="form-control rounded-3 border-light-subtle bg-light-subtle small py-2" value={formData.price} onChange={e => setFormData({...formData, price: e.target.value})} placeholder="0.00" required />
                    </div>
                    <div className="col-6">
                      <label className="text-muted small fw-bold text-uppercase mb-1">Validity (Days)</label>
                      <input type="number" className="form-control rounded-3 border-light-subtle bg-light-subtle small py-2" value={formData.duration_days} onChange={e => setFormData({...formData, duration_days: e.target.value})} required />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="text-muted small fw-bold text-uppercase mb-1">Instance Limit</label>
                    <div className="input-group">
                        <span className="input-group-text bg-light-subtle border-light-subtle text-muted small"><i className="fas fa-microchip"></i></span>
                        <input type="number" className="form-control rounded-end-3 border-light-subtle bg-light-subtle small py-2" value={formData.device_limit} onChange={e => setFormData({...formData, device_limit: e.target.value})} placeholder="Active Nodes" required />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-muted small fw-bold text-uppercase mb-1">Message Limit</label>
                    <div className="input-group">
                        <span className="input-group-text bg-light-subtle border-light-subtle text-muted small"><i className="fas fa-paper-plane"></i></span>
                        <input type="number" className="form-control rounded-end-3 border-light-subtle bg-light-subtle small py-2" value={formData.message_limit} onChange={e => setFormData({...formData, message_limit: e.target.value})} placeholder="Max Messages" required />
                    </div>
                  </div>

                  <button className="btn btn-primary w-100 rounded-pill fw-bold shadow-sm border-0 py-2">
                    SAVE SUBSCRIPTION PLAN
                  </button>
                </form>
              </div>
            </div>

            {/* Existing Plans Table - Fixed Layout Table Structure */}
            <div className="col-lg-8">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead style={{ backgroundColor: '#f8fafc' }}>
                      <tr className="text-muted small text-uppercase fw-bold">
                        <th className="ps-4 py-4">Identity</th>
                        <th className="py-4">Pricing</th>
                        <th className="py-4">Resources</th>
                        <th className="py-4 text-center">Status</th>
                        <th className="pe-4 py-4 text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan="5" className="text-center py-5">Loading Master Plans...</td></tr>
                      ) : plans.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-5">No plans configured yet.</td></tr>
                      ) : (
                        plans.map(p => (
                          <tr key={p.id} className="border-bottom border-light">
                            <td className="ps-4 py-3">
                              <div className="d-flex align-items-center gap-3">
                                <div className="rounded-4 bg-primary text-white d-flex align-items-center justify-content-center shadow-sm" style={{ width: '42px', height: '42px' }}>
                                  <i className="fas fa-crown small"></i>
                                </div>
                                <div>
                                  <div className="fw-bold text-dark small mb-0">{p.name}</div>
                                  <div className="text-muted" style={{ fontSize: '11px' }}>Validity: {p.duration_days} Days</div>
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className="fw-bold text-success small">Rs.{p.price}</div>
                              <div className="text-muted x-small" style={{ fontSize: '10px' }}>Per Cycle</div>
                            </td>
                            <td>
                              <div className="small text-dark fw-medium">Nodes: {p.device_limit}</div>
                              <div className="text-muted x-small" style={{ fontSize: '10px' }}>Messages: {p.message_limit}</div>
                            </td>
                            <td className="text-center">
                              <span className="badge rounded-pill fw-bold bg-success-subtle text-success px-3 py-2" style={{ fontSize: '10px' }}>
                                <span className="pulse-dot me-2 bg-success"></span>
                                ACTIVE
                              </span>
                            </td>
                            <td className="pe-4 text-end">
                                <button className="btn btn-sm text-danger border-0">
                                    <i className="fas fa-trash-alt fs-6"></i>
                                </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageTransition>
    </Layout>
  );
}